/**
 * Pure classification of a loaded glTF mesh into the render role it should get.
 *
 * Node names are harvested from the real assets in public/models/cars/, which
 * fall into two families:
 *   - richly named (Shelby: 'GT500.Wheel.Ft.R', Camaro: 'Wheel_Back_R')
 *   - generic    (Mustang: 'Object_12' ... 'Object_58')
 *
 * So the material names carry equal weight to the node names: on the Mustang
 * the role is only recoverable from 'carpaint' / 'tire' / 'brakedisk' / glass.
 */

export type MeshRole = 'body' | 'wheel_rim' | 'tire' | 'brake' | 'glass' | 'other';

export interface MeshNaming {
  nodeName: string;
  parentName: string;
  materialNames: string[];
}

export interface MeshRoleRules {
  bodyKeywords: string[];
  wheelKeywords: string[];
  tireKeywords: string[];
  brakeKeywords: string[];
  glassKeywords: string[];
}

/**
 * Order matters. Tire and brake are tested before wheel_rim because in every
 * one of these assets the tire/brake material sits on the same node as the rim
 * ('GT500.Wheel.Ft.R' carries both 'lambert1' rim and 'tire' sidewall parts),
 * so a wheel-first test would paint sidewalls with rim chrome.
 */
export function classifyMeshRole(naming: MeshNaming, rules: MeshRoleRules): MeshRole {
  const haystack = [naming.nodeName, naming.parentName, ...naming.materialNames]
    .join(' ')
    .toLowerCase();

  const matches = (keywords: string[]): boolean =>
    keywords.some((keyword) => haystack.includes(keyword.toLowerCase()));

  if (matches(rules.tireKeywords)) return 'tire';
  if (matches(rules.brakeKeywords)) return 'brake';
  if (matches(rules.glassKeywords)) return 'glass';
  if (matches(rules.wheelKeywords)) return 'wheel_rim';
  if (matches(rules.bodyKeywords)) return 'body';
  return 'other';
}